import { localStorage } from 'dibs-browser-storage';
import { getBuyerId } from 'dibs-cookie-jar';
import { type State, type ActionType } from './GetHelpZendeskTypes.ts';
import {
    STEPS,
    ACTIONS,
    RENDER_AS,
    GET_HELP_MODAL_ZENDESK_OPTIONS,
} from './GetHelpZendeskConstants.ts';
import { type GetHelpModalZendeskOptions } from './GetHelpZendeskTypes.ts';
import { getGetHelpZendeskLocalStorageState } from './getHelpZendeskHelpers.ts';

const setLocalStorageState = (newState: Partial<GetHelpModalZendeskOptions>): void => {
    localStorage.setItem(GET_HELP_MODAL_ZENDESK_OPTIONS, {
        ...getGetHelpZendeskLocalStorageState(),
        ...newState,
    });
};

export const initialState: State = {
    step: STEPS.INTRO,
    stepsPath: [{ step: STEPS.INTRO }],
    renderAs: RENDER_AS.CONSUMER,
    onClose: () => {},
    isOpen: false,
    isEmbedded: false,
    userId: '',
    placement: 'globalNav',
    sellerId: '',
    isSeller: false,
    orderId: '',
    articleId: '',
    searchQuery: '',
    showFurtherSupport: false,
    contactSupportSearchQuery: '',
    itemId: '',
};

const scrollToTop = (): void => {
    const modalElement = document.querySelector(`[data-tn='get-help-zendesk-modal']`);

    if (modalElement) {
        modalElement.scrollTop = 0;
    }
};

export function getHelpZendeskReducer(state: State, action: ActionType): State {
    switch (action.type) {
        case ACTIONS.SET_STEP: {
            scrollToTop();

            let stepsPath: State['stepsPath'] = [];

            if (action.step === 'noSearchResults') {
                state.stepsPath.pop();
            }

            stepsPath = [...state.stepsPath, { step: action.step }];

            let updatedState: Partial<State> = {
                step: action.step,
                stepsPath,
            };

            if (state.step === 'article') {
                updatedState = { ...updatedState, topic: '' };
            }

            setLocalStorageState(updatedState);

            return {
                ...state,
                ...updatedState,
            };
        }
        case ACTIONS.OPEN_ARTICLE: {
            scrollToTop();
            const updatedState = {
                articleId: action.articleId,
                step: STEPS.ARTICLE,
                stepsPath: [
                    ...state.stepsPath,
                    { step: STEPS.ARTICLE, articleId: action.articleId },
                ],
                searchQuery: '',
            };

            setLocalStorageState(updatedState);

            return {
                ...state,
                ...updatedState,
            };
        }
        case ACTIONS.GO_BACK: {
            scrollToTop();
            const previousStep = state.stepsPath.slice(-1)[0] || {};
            state.stepsPath.pop();
            const currentStep = state.stepsPath.slice(-1)[0] || {};
            let updatedState = {};

            if (currentStep.step === STEPS.ARTICLE) {
                updatedState = { articleId: currentStep?.articleId || state.articleId };
            }

            if (previousStep.step === STEPS.CONTACT_SUPPORT_SEARCH) {
                updatedState = {
                    ...updatedState,
                    showFurtherSupport: false,
                    displayFooter: false,
                    contactSupportSearchQuery: '',
                };
            }

            if (currentStep.step === STEPS.CONTACT_SUPPORT_SEARCH) {
                updatedState = {
                    ...updatedState,
                    displayFooter: !!state.contactSupportSearchQuery,
                };
            }

            if (currentStep.step === STEPS.SEARCH_RESULTS) {
                updatedState = { searchQuery: currentStep?.searchQuery || state.searchQuery };
            } else {
                updatedState = { ...updatedState, searchQuery: '' };
            }

            updatedState = {
                ...updatedState,
                stepsPath: state.stepsPath,
                step: currentStep.step || STEPS.INTRO,
            };

            setLocalStorageState(updatedState);

            return {
                ...state,
                ...updatedState,
            };
        }
        case ACTIONS.SET_ORDER_ID: {
            setLocalStorageState({ orderId: action.orderId });

            return {
                ...state,
                orderId: action.orderId,
            };
        }
        case ACTIONS.SET_CATEGORY_ID: {
            setLocalStorageState({ categoryId: action.categoryId });

            return {
                ...state,
                categoryId: action.categoryId,
            };
        }
        case ACTIONS.SET_CMS_TOPIC_KEY: {
            setLocalStorageState({ topicKey: action.topicKey });

            return {
                ...state,
                topicKey: action.topicKey,
            };
        }
        case ACTIONS.SET_DISPLAY_FOOTER: {
            return {
                ...state,
                displayFooter: action.value,
            };
        }
        case ACTIONS.SET_CONTACT_SUPPORT_SEARCH_QUERY: {
            setLocalStorageState({ contactSupportSearchQuery: action.query });

            return {
                ...state,
                contactSupportSearchQuery: action.query,
            };
        }
        case ACTIONS.SET_SHOW_FURTHER_SUPPORT: {
            setLocalStorageState({ showFurtherSupport: action.value });

            return {
                ...state,
                showFurtherSupport: action.value,
            };
        }
        case ACTIONS.SET_SEARCH_RESULTS_STEP: {
            scrollToTop();
            let updatedState = {};

            updatedState = {
                ...updatedState,
                searchQuery: action.searchQuery,
                step: STEPS.SEARCH_RESULTS,
                stepsPath: [
                    ...state.stepsPath,
                    {
                        step: STEPS.SEARCH_RESULTS,
                        searchQuery: action.searchQuery,
                    },
                ],
            };

            setLocalStorageState(updatedState);

            return {
                ...state,
                ...updatedState,
            };
        }

        case ACTIONS.SET_BUYER: {
            const userId = getBuyerId(document.cookie);

            return {
                ...state,
                ...(userId && {
                    userId,
                    renderAs: 'consumer',
                }),
            };
        }
        case ACTIONS.SET_ITEM_ID: {
            setLocalStorageState({ itemId: action.itemId });

            return {
                ...state,
                itemId: action.itemId,
            };
        }
        default: {
            return state;
        }
    }
}
